import { useCallback, useEffect, useState } from "react";
import { Lock, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, USER_ID } from "./api";
import { SectionTitle, rewardToast } from "./components";
import { useT } from "./i18n";

const GOLD_TOAST = { background: "#1A1A1A", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" };

const RULES_KEY = "xp_quiz_rules_seen";

const PointsExplainer = ({ onDismiss }) => {
  const t = useT();
  const rows = [
    ["+5", t("quizRules.r1"), "text-[#D4AF37]"],
    ["+20", t("quizRules.r2"), "text-[#F0C742]"],
    ["−5", t("quizRules.r3"), "text-[#E25822]"],
  ];
  return (
    <div data-testid="quiz-rules-card" className="rounded-2xl border border-[#D4AF37]/50 bg-[#121212] p-5 story-pop">
      <p className="flex items-center gap-1.5 text-[10px] tracking-[0.3em] text-[#D4AF37] font-bold mb-3">
        <Sparkles size={12} /> {t("quizRules.title")}
      </p>
      <div className="space-y-2.5">
        {rows.map(([pts, text, cls]) => (
          <div key={pts} className="flex items-center gap-3">
            <span className={`w-11 shrink-0 text-right font-black text-base ${cls}`}>{pts}</span>
            <span className="text-xs text-white/75">{text}</span>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-white/50 mt-3">{t("quizRules.r4")}</p>
      <button
        data-testid="quiz-rules-got-it"
        onClick={onDismiss}
        className="w-full mt-4 bg-[#D4AF37] text-black text-[11px] font-black rounded-full py-3 tracking-widest hover:bg-[#F1C40F]"
      >
        {t("quizRules.cta")}
      </button>
    </div>
  );
};

export default function QuizLadder({ slug }) {
  const t = useT();
  const [data, setData] = useState(null);
  const [activeQid, setActiveQid] = useState(null);
  const [picked, setPicked] = useState(null);
  const [result, setResult] = useState(null);
  const [code, setCode] = useState("");
  const [codeErr, setCodeErr] = useState(false);
  const [showRules, setShowRules] = useState(() => !localStorage.getItem(RULES_KEY));

  const load = useCallback(
    () => api.get(`/quiz/ladder/${slug}`, { params: { user_id: USER_ID } }).then((r) => setData(r.data)).catch(() => {}),
    [slug]
  );
  useEffect(() => { load(); }, [load]);
  if (!data) return null;

  const answer = async (qid, i) => {
    setActiveQid(qid);
    setPicked(i);
    try {
      const r = await api.post("/quiz/answer", { user_id: USER_ID, qid, answer: i, table: localStorage.getItem("xp_table") || "" });
      setResult(r.data);
      rewardToast(r.data);
      if (r.data.correct) {
        const lv = data.levels.find((l) => l.question && l.question.qid === qid);
        const completed = lv && lv.solved + 1 >= lv.total;
        window.dispatchEvent(new CustomEvent("xp-mascot", { detail: { type: completed ? "quiz-level-complete" : "quiz-correct", level: lv?.level } }));
      }
      if (r.data.correct && !r.data.awarded && r.data.capped) toast(t("quiz.capped"), { style: GOLD_TOAST });
    } catch {
      setActiveQid(null);
      setPicked(null);
    }
  };

  const next = () => {
    setActiveQid(null);
    setPicked(null);
    setResult(null);
    load();
  };

  const unlock = async () => {
    try {
      await api.post("/quiz/unlock", { user_id: USER_ID, code });
      setCodeErr(false);
      setCode("");
      load();
    } catch {
      setCodeErr(true);
    }
  };

  return (
    <section className="px-4 pb-6" data-testid="quiz-ladder">
      <SectionTitle kicker={t("dish.quizKicker")} title={t("dish.quizTitle")} testId="section-quiz" />
      {showRules ? (
        <PointsExplainer onDismiss={() => { localStorage.setItem(RULES_KEY, "1"); setShowRules(false); }} />
      ) : (
      <>
      <button
        data-testid="quiz-rules-open"
        onClick={() => setShowRules(true)}
        className="mb-3 flex items-center gap-1.5 text-[9px] font-bold tracking-widest text-[#D4AF37]/80 border border-[#D4AF37]/30 rounded-full px-3 py-1.5"
      >
        <Sparkles size={10} /> {t("quizRules.open")}
      </button>
      <div className="space-y-3">
        {data.levels.map((lv) => {
          const isActive = lv.question && activeQid === lv.question.qid;
          return (
            <div
              key={lv.level}
              data-testid={`quiz-level-${lv.level}`}
              className={`rounded-2xl border p-4 ${lv.done ? "border-[#D4AF37]/40 bg-[#D4AF37]/5" : "border-white/10 bg-[#121212]"}`}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] tracking-[0.25em] text-[#D4AF37] font-bold">
                  {`LEVEL ${lv.level} · ${t(`quiz.l${lv.level}`)}`}
                </p>
                <span className="text-[10px] font-black shrink-0"><span className="text-[#D4AF37]">{`+${lv.points}`}</span><span className="text-white/40">{` / −${lv.penalty || 5} PTS`}</span></span>
              </div>
              {lv.done && (
                <p data-testid={`quiz-level-${lv.level}-done`} className="flex items-center gap-1.5 text-xs text-[#D4AF37] mt-2">
                  <Check size={13} /> {t("quiz.done")} · {lv.solved}/{lv.total}
                </p>
              )}
              {lv.locked && !lv.done && (
                <div data-testid="quiz-l3-locked" className="mt-2">
                  <p className="flex items-center gap-1.5 text-xs text-white/60">
                    <Lock size={13} className="text-[#D4AF37]" /> {t("quiz.lockedDesc")}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <input
                      data-testid="quiz-code-input"
                      value={code}
                      onChange={(e) => { setCode(e.target.value); setCodeErr(false); }}
                      onKeyDown={(e) => e.key === "Enter" && unlock()}
                      placeholder={t("quiz.codePlaceholder")}
                      className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/15 rounded-full px-4 py-2.5 text-xs uppercase tracking-widest focus:outline-none focus:border-[#D4AF37]/60"
                    />
                    <button
                      data-testid="quiz-unlock-btn"
                      onClick={unlock}
                      disabled={!code.trim()}
                      className="bg-[#D4AF37] disabled:bg-[#1A1A1A] disabled:text-white/40 text-black text-[10px] font-bold rounded-full px-5 tracking-widest"
                    >
                      {t("quiz.unlock")}
                    </button>
                  </div>
                  {codeErr && <p data-testid="quiz-code-error" className="text-[10px] text-[#E25822] mt-2">{t("quiz.wrongCode")}</p>}
                </div>
              )}
              {lv.question && (
                <div className="mt-3">
                  <p className="text-sm font-semibold mb-3" data-testid={`quiz-q-${lv.level}`}>{lv.question.q}</p>
                  <div className="space-y-2">
                    {lv.question.options.map((opt, i) => {
                      let cls = "border-white/10 bg-[#1A1A1A] hover:border-[#D4AF37]/40";
                      if (isActive && result) {
                        if (i === result.correct_index) cls = "border-green-500 bg-green-500/10";
                        else if (i === picked) cls = "border-[#8B3A3A] bg-[#8B3A3A]/10";
                      } else if (isActive && picked === i) {
                        cls = "border-[#D4AF37] bg-[#D4AF37]/10";
                      }
                      return (
                        <button
                          key={i}
                          data-testid={`quiz-l${lv.level}-option-${i}`}
                          disabled={result !== null}
                          onClick={() => answer(lv.question.qid, i)}
                          className={`w-full text-left rounded-xl border px-4 py-3 text-sm transition-colors ${cls}`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  {isActive && result && (
                    <>
                      <p data-testid="quiz-feedback" className={`text-xs mt-3 ${result.correct ? "text-green-400" : "text-[#E25822]"}`}>
                        {result.correct
                          ? `${t("quiz.correct")} ${result.awarded ? `+${result.points} PTS` : result.month_locked ? t("quiz.monthLocked") : result.capped ? t("quiz.capped") : t("quiz.already")}`
                          : `${t("quiz.wrong")}${result.penalty > 0 ? ` −${result.penalty} PTS` : result.month_locked ? ` · ${t("quiz.monthLocked")}` : ""}`}
                      </p>
                      <button
                        data-testid="quiz-next-btn"
                        onClick={next}
                        className="w-full mt-3 border border-[#D4AF37]/50 text-[#D4AF37] text-[10px] font-bold rounded-full py-2.5 tracking-widest"
                      >
                        {t("quiz.next")}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      </>
      )}
    </section>
  );
}
